import { getFxGpuModule, type FxGpuModule, type FxGpuUniforms } from "./fxGpuModules";

export type FxGpuSource =
  | HTMLVideoElement
  | HTMLImageElement
  | HTMLCanvasElement
  | ImageBitmap
  | OffscreenCanvas;

type WebGlProgramEntry = {
  program: WebGLProgram;
  attribPosition: number;
  attribTexCoord: number;
  uniformTexture: WebGLUniformLocation;
  uniformResolution: WebGLUniformLocation;
  uniformTime: WebGLUniformLocation;
  uniformIntensity: WebGLUniformLocation;
  uniformProgress: WebGLUniformLocation;
  uniformSeed: WebGLUniformLocation;
  uniformAmp: WebGLUniformLocation;
  uniformSpeed: WebGLUniformLocation;
  texture: WebGLTexture;
};

type WebGpuDeviceLike = {
  queue: {
    submit: (buffers: unknown[]) => void;
    writeBuffer: (buffer: unknown, offset: number, data: ArrayBuffer) => void;
    copyExternalImageToTexture: (source: unknown, dest: unknown, size: [number, number, number]) => void;
  };
  createShaderModule: (args: { code: string }) => unknown;
  createRenderPipeline: (args: unknown) => unknown;
  createBuffer: (args: { size: number; usage: number }) => unknown;
  createTexture: (args: { size: [number, number, number]; format: string; usage: number }) => unknown;
  createSampler: (args?: unknown) => unknown;
  createBindGroup: (args: unknown) => unknown;
  createCommandEncoder: () => {
    beginRenderPass: (args: unknown) => {
      setPipeline: (pipeline: unknown) => void;
      setBindGroup: (index: number, bindGroup: unknown) => void;
      draw: (vertexCount: number) => void;
      end: () => void;
    };
    finish: () => unknown;
  };
};

type WebGpuContextLike = {
  configure: (args: { device: WebGpuDeviceLike; format: string; alphaMode?: string }) => void;
  getCurrentTexture: () => { createView: () => unknown };
};

type WebGpuProgramEntry = {
  pipeline: unknown;
  bindGroup: unknown;
  uniformBuffer: unknown;
  inputTexture: unknown;
  inputView: unknown;
  sampler: unknown;
  size: [number, number];
  format: string;
};

type WebGpuState = {
  device: WebGpuDeviceLike;
  context: WebGpuContextLike;
  format: string;
  programById: Map<string, WebGpuProgramEntry>;
  ready: boolean;
  failed: boolean;
  initPromise: Promise<void> | null;
};

const GPU_BUFFER_USAGE_UNIFORM = 0x10;
const GPU_BUFFER_USAGE_COPY_DST = 0x20;
const GPU_TEXTURE_USAGE_TEXTURE_BINDING = 0x04;
const GPU_TEXTURE_USAGE_COPY_DST = 0x02;
const GPU_TEXTURE_USAGE_RENDER_ATTACHMENT = 0x10;

const resolveWebGpuBufferUsage = (): { uniform: number; copyDst: number } => {
  const usage = (globalThis as unknown as { GPUBufferUsage?: { UNIFORM: number; COPY_DST: number } }).GPUBufferUsage;
  return {
    uniform: usage?.UNIFORM ?? GPU_BUFFER_USAGE_UNIFORM,
    copyDst: usage?.COPY_DST ?? GPU_BUFFER_USAGE_COPY_DST,
  };
};

const resolveWebGpuTextureUsage = (): { textureBinding: number; copyDst: number; renderAttachment: number } => {
  const usage = (globalThis as unknown as { GPUTextureUsage?: { TEXTURE_BINDING: number; COPY_DST: number; RENDER_ATTACHMENT: number } }).GPUTextureUsage;
  return {
    textureBinding: usage?.TEXTURE_BINDING ?? GPU_TEXTURE_USAGE_TEXTURE_BINDING,
    copyDst: usage?.COPY_DST ?? GPU_TEXTURE_USAGE_COPY_DST,
    renderAttachment: usage?.RENDER_ATTACHMENT ?? GPU_TEXTURE_USAGE_RENDER_ATTACHMENT,
  };
};

const VERTEX_SHADER_SOURCE = `
attribute vec2 a_position;
attribute vec2 a_texCoord;
varying vec2 v_uv;
void main() {
  v_uv = a_texCoord;
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

const WGSL_VERTEX = `
@vertex
fn vsMain(@builtin(vertex_index) index: u32) -> @builtin(position) vec4<f32> {
  var pos = array<vec2<f32>, 6>(
    vec2<f32>(-1.0, -1.0),
    vec2<f32>( 1.0, -1.0),
    vec2<f32>(-1.0,  1.0),
    vec2<f32>(-1.0,  1.0),
    vec2<f32>( 1.0, -1.0),
    vec2<f32>( 1.0,  1.0)
  );
  return vec4<f32>(pos[index], 0.0, 1.0);
}
`;

const WEBGL_RETRY_DELAY_MS = 45_000;
const WEBGPU_RETRY_DELAY_MS = 45_000;
let webglBlockedUntilMs = 0;
let webgpuBlockedUntilMs = 0;
let webglFailureLogged = false;
let webgpuFailureLogged = false;

function getNowMs(): number {
  if (typeof performance !== "undefined") return performance.now();
  return Date.now();
}

function markWebGlUnavailable(reason: string): void {
  webglBlockedUntilMs = Math.max(webglBlockedUntilMs, getNowMs() + WEBGL_RETRY_DELAY_MS);
  if (webglFailureLogged) return;
  webglFailureLogged = true;
  try {
    console.warn(`[FX GPU] WebGL unavailable; falling back to CPU for ${Math.round(WEBGL_RETRY_DELAY_MS / 1000)}s. reason=${reason}`);
  } catch {}
}

function markWebGpuUnavailable(reason: string): void {
  webgpuBlockedUntilMs = Math.max(webgpuBlockedUntilMs, getNowMs() + WEBGPU_RETRY_DELAY_MS);
  if (webgpuFailureLogged) return;
  webgpuFailureLogged = true;
  try {
    console.warn(`[FX GPU] WebGPU unavailable; falling back to WebGL/CPU for ${Math.round(WEBGPU_RETRY_DELAY_MS / 1000)}s. reason=${reason}`);
  } catch {}
}

export class FxGpuRenderer {
  private canvas: HTMLCanvasElement;
  private gl: WebGLRenderingContext | null = null;
  private webglPrograms = new Map<string, WebGlProgramEntry>();
  private webglBuffer: WebGLBuffer | null = null;
  private webgpu: WebGpuState | null = null;
  private preferWebGpu = true;
  private webglContextLost = false;
  private readonly onWebGlContextLost = (event: Event) => {
    const asWebGlEvent = event as Event & { preventDefault?: () => void };
    asWebGlEvent.preventDefault?.();
    this.webglContextLost = true;
    markWebGlUnavailable("context-lost");
    this.disposeWebGlResources(false);
    // Notify the UI so it can show a toast — GPU effects will be dark for 45s otherwise
    try {
      window.dispatchEvent(new CustomEvent("vb:gpu-context-lost"));
    } catch {}
  };
  private readonly onWebGlContextRestored = () => {
    this.webglContextLost = false;
  };

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.canvas.addEventListener("webglcontextlost", this.onWebGlContextLost as EventListener, false);
    this.canvas.addEventListener("webglcontextrestored", this.onWebGlContextRestored as EventListener, false);
  }

  dispose(): void {
    this.canvas.removeEventListener("webglcontextlost", this.onWebGlContextLost as EventListener, false);
    this.canvas.removeEventListener("webglcontextrestored", this.onWebGlContextRestored as EventListener, false);
    this.disposeWebGlResources(true);
    this.disposeWebGpuResources();
  }

  render(args: {
    moduleId: string | null | undefined;
    source: FxGpuSource;
    uniforms: FxGpuUniforms;
  }): boolean {
    const { moduleId, source, uniforms } = args;
    const module = getFxGpuModule(moduleId);
    if (!module) return false;

    if (this.preferWebGpu) {
      const webgpuOk = this.renderWebGpu(module, source, uniforms);
      if (webgpuOk) return true;
    }

    return this.renderWebGl(module, source, uniforms);
  }

  private renderWebGl(module: FxGpuModule, source: FxGpuSource, uniforms: FxGpuUniforms): boolean {
    const gl = this.ensureWebGl();
    if (!gl) return false;

    const programEntry = this.ensureWebGlProgram(gl, module);
    if (!programEntry) return false;

    try {
      const width = Math.max(1, this.canvas.width);
      const height = Math.max(1, this.canvas.height);
      gl.viewport(0, 0, width, height);

      gl.useProgram(programEntry.program);

      gl.bindTexture(gl.TEXTURE_2D, programEntry.texture);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 1);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);

      gl.uniform1i(programEntry.uniformTexture, 0);
      gl.uniform2f(programEntry.uniformResolution, uniforms.resolution[0], uniforms.resolution[1]);
      gl.uniform1f(programEntry.uniformTime, uniforms.time);
      gl.uniform1f(programEntry.uniformIntensity, uniforms.intensity);
      gl.uniform1f(programEntry.uniformProgress, uniforms.progress);
      gl.uniform1f(programEntry.uniformSeed, uniforms.seed);
      gl.uniform1f(programEntry.uniformAmp, uniforms.amplitude);
      gl.uniform1f(programEntry.uniformSpeed, uniforms.speed);

      gl.bindBuffer(gl.ARRAY_BUFFER, this.webglBuffer);
      gl.enableVertexAttribArray(programEntry.attribPosition);
      gl.vertexAttribPointer(programEntry.attribPosition, 2, gl.FLOAT, false, 16, 0);
      gl.enableVertexAttribArray(programEntry.attribTexCoord);
      gl.vertexAttribPointer(programEntry.attribTexCoord, 2, gl.FLOAT, false, 16, 8);

      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      webglFailureLogged = false;
      return true;
    } catch {
      markWebGlUnavailable("render-failed");
      this.disposeWebGlResources(false);
      return false;
    }
  }

  private ensureWebGl(): WebGLRenderingContext | null {
    if (this.webglContextLost) return null;
    if (getNowMs() < webglBlockedUntilMs) return null;
    if (this.gl) return this.gl;
    let gl: WebGLRenderingContext | null = null;
    try {
      gl = this.canvas.getContext("webgl", { premultipliedAlpha: true }) as WebGLRenderingContext | null;
    } catch {
      markWebGlUnavailable("context-create-threw");
      return null;
    }
    if (!gl) {
      markWebGlUnavailable("context-create-failed");
      return null;
    }
    this.gl = gl;
    this.webglBuffer = gl.createBuffer();
    if (this.webglBuffer) {
      gl.bindBuffer(gl.ARRAY_BUFFER, this.webglBuffer);
      const verts = new Float32Array([
        -1, -1, 0, 0,
         1, -1, 1, 0,
        -1,  1, 0, 1,
        1,  1, 1, 1,
      ]);
      gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
    }
    webglFailureLogged = false;
    return this.gl;
  }

  private ensureWebGlProgram(gl: WebGLRenderingContext, module: FxGpuModule): WebGlProgramEntry | null {
    const cached = this.webglPrograms.get(module.id);
    if (cached) return cached;

    const vertexShader = this.compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER_SOURCE);
    const fragmentShader = this.compileShader(gl, gl.FRAGMENT_SHADER, module.webglFragment);
    if (!vertexShader || !fragmentShader) return null;

    const program = gl.createProgram();
    if (!program) return null;
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      gl.deleteProgram(program);
      return null;
    }

    const attribPosition = gl.getAttribLocation(program, "a_position");
    const attribTexCoord = gl.getAttribLocation(program, "a_texCoord");
    const uniformTexture = gl.getUniformLocation(program, "u_texture");
    const uniformResolution = gl.getUniformLocation(program, "u_resolution");
    const uniformTime = gl.getUniformLocation(program, "u_time");
    const uniformIntensity = gl.getUniformLocation(program, "u_intensity");
    const uniformProgress = gl.getUniformLocation(program, "u_progress");
    const uniformSeed = gl.getUniformLocation(program, "u_seed");
    const uniformAmp = gl.getUniformLocation(program, "u_amp");
    const uniformSpeed = gl.getUniformLocation(program, "u_speed");

    if (!uniformTexture || !uniformResolution || !uniformTime || !uniformIntensity || !uniformProgress || !uniformSeed || !uniformAmp || !uniformSpeed) {
      gl.deleteProgram(program);
      return null;
    }

    const texture = gl.createTexture();
    if (!texture) {
      gl.deleteProgram(program);
      return null;
    }
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    const entry: WebGlProgramEntry = {
      program,
      attribPosition,
      attribTexCoord,
      uniformTexture,
      uniformResolution,
      uniformTime,
      uniformIntensity,
      uniformProgress,
      uniformSeed,
      uniformAmp,
      uniformSpeed,
      texture,
    };
    this.webglPrograms.set(module.id, entry);
    return entry;
  }

  private compileShader(gl: WebGLRenderingContext, type: number, source: string): WebGLShader | null {
    const shader = gl.createShader(type);
    if (!shader) return null;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      gl.deleteShader(shader);
      return null;
    }
    return shader;
  }

  private renderWebGpu(module: FxGpuModule, source: FxGpuSource, uniforms: FxGpuUniforms): boolean {
    if (!module.webgpuFragment) return false;
    const state = this.ensureWebGpuState();
    if (!state || state.failed || !state.ready) return false;

    const size: [number, number] = [Math.max(1, this.canvas.width), Math.max(1, this.canvas.height)];
    const program = this.ensureWebGpuProgram(state, module, size);
    if (!program) return false;

    const device = state.device;
    const context = state.context;

    const textureUsage = resolveWebGpuTextureUsage();
    if (program.size[0] !== size[0] || program.size[1] !== size[1]) {
      program.size = size;
      program.inputTexture = device.createTexture({
        size: [size[0], size[1], 1],
        format: state.format,
        usage: textureUsage.textureBinding | textureUsage.copyDst,
      });
      program.inputView = (program.inputTexture as { createView: () => unknown }).createView();
      program.bindGroup = device.createBindGroup({
        layout: (program.pipeline as { getBindGroupLayout: (index: number) => unknown }).getBindGroupLayout(0),
        entries: [
          { binding: 0, resource: program.inputView },
          { binding: 1, resource: program.sampler },
          { binding: 2, resource: { buffer: program.uniformBuffer } },
        ],
      });
    }

    const uniformData = new Float32Array([
      uniforms.resolution[0],
      uniforms.resolution[1],
      uniforms.time,
      uniforms.intensity,
      uniforms.progress,
      uniforms.seed,
      uniforms.amplitude,
      uniforms.speed,
    ]);
    try {
      device.queue.writeBuffer(program.uniformBuffer, 0, uniformData.buffer);

      device.queue.copyExternalImageToTexture(
        { source },
        { texture: program.inputTexture },
        [size[0], size[1], 1]
      );

      const colorView = context.getCurrentTexture().createView();
      const encoder = device.createCommandEncoder();
      const pass = encoder.beginRenderPass({
        colorAttachments: [
          {
            view: colorView,
            loadOp: "clear",
            storeOp: "store",
            clearValue: { r: 0, g: 0, b: 0, a: 0 },
          },
        ],
      });
      pass.setPipeline(program.pipeline);
      pass.setBindGroup(0, program.bindGroup);
      pass.draw(6);
      pass.end();
      device.queue.submit([encoder.finish()]);
      webgpuFailureLogged = false;
      return true;
    } catch {
      markWebGpuUnavailable("render-failed");
      this.disposeWebGpuResources();
      this.preferWebGpu = false;
      return false;
    }
  }

  private ensureWebGpuState(): WebGpuState | null {
    if (getNowMs() < webgpuBlockedUntilMs) return null;
    if (this.webgpu) {
      if (!this.webgpu.ready && !this.webgpu.failed && this.webgpu.initPromise) {
        return this.webgpu;
      }
      return this.webgpu;
    }

    const gpu = (navigator as unknown as { gpu?: { requestAdapter: (opts?: unknown) => Promise<unknown> } }).gpu;
    if (!gpu) {
      this.webgpu = { device: {} as WebGpuDeviceLike, context: {} as WebGpuContextLike, format: "bgra8unorm", programById: new Map(), ready: false, failed: true, initPromise: null };
      return this.webgpu;
    }

    let context: WebGpuContextLike | null = null;
    try {
      context = this.canvas.getContext("webgpu") as WebGpuContextLike | null;
    } catch {
      markWebGpuUnavailable("context-create-threw");
      this.preferWebGpu = false;
      return null;
    }
    if (!context) {
      markWebGpuUnavailable("context-create-failed");
      this.preferWebGpu = false;
      this.webgpu = { device: {} as WebGpuDeviceLike, context: {} as WebGpuContextLike, format: "bgra8unorm", programById: new Map(), ready: false, failed: true, initPromise: null };
      return this.webgpu;
    }

    const state: WebGpuState = {
      device: {} as WebGpuDeviceLike,
      context,
      format: "bgra8unorm",
      programById: new Map(),
      ready: false,
      failed: false,
      initPromise: null,
    };

    state.initPromise = gpu.requestAdapter().then((adapter) => {
      if (!adapter) {
        state.failed = true;
        markWebGpuUnavailable("adapter-missing");
        return;
      }
      return (adapter as { requestDevice: () => Promise<WebGpuDeviceLike> }).requestDevice().then((device) => {
        state.device = device;
        state.format = "bgra8unorm";
        context!.configure({ device, format: state.format, alphaMode: "premultiplied" });
        state.ready = true;
        webgpuFailureLogged = false;
      });
    }).catch(() => {
      state.failed = true;
      markWebGpuUnavailable("device-init-failed");
    });

    this.webgpu = state;
    return state;
  }

  private ensureWebGpuProgram(state: WebGpuState, module: FxGpuModule, size: [number, number]): WebGpuProgramEntry | null {
    const cached = state.programById.get(module.id);
    if (cached) return cached;
    if (!state.ready) return null;

    const device = state.device;
    const bufferUsage = resolveWebGpuBufferUsage();
    const textureUsage = resolveWebGpuTextureUsage();

    const shaderModule = device.createShaderModule({
      code: `${WGSL_VERTEX}\n${module.webgpuFragment}`,
    });

    const pipeline = device.createRenderPipeline({
      layout: "auto",
      vertex: { module: shaderModule, entryPoint: "vsMain" },
      fragment: { module: shaderModule, entryPoint: "fsMain", targets: [{ format: state.format }] },
      primitive: { topology: "triangle-list" },
    });

    const uniformBuffer = device.createBuffer({
      size: 32,
      usage: bufferUsage.uniform | bufferUsage.copyDst,
    });

    const inputTexture = device.createTexture({
      size: [size[0], size[1], 1],
      format: state.format,
      usage: textureUsage.textureBinding | textureUsage.copyDst,
    });

    const inputView = (inputTexture as { createView: () => unknown }).createView();
    const sampler = device.createSampler({});

    const bindGroup = device.createBindGroup({
      layout: (pipeline as { getBindGroupLayout: (index: number) => unknown }).getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: inputView },
        { binding: 1, resource: sampler },
        { binding: 2, resource: { buffer: uniformBuffer } },
      ],
    });

    const entry: WebGpuProgramEntry = {
      pipeline,
      bindGroup,
      uniformBuffer,
      inputTexture,
      inputView,
      sampler,
      size,
      format: state.format,
    };
    state.programById.set(module.id, entry);
    return entry;
  }

  private disposeWebGlResources(loseContext: boolean): void {
    const gl = this.gl;
    if (gl) {
      for (const entry of this.webglPrograms.values()) {
        try {
          gl.deleteTexture(entry.texture);
        } catch {}
        try {
          gl.deleteProgram(entry.program);
        } catch {}
      }
      if (this.webglBuffer) {
        try {
          gl.deleteBuffer(this.webglBuffer);
        } catch {}
      }
      if (loseContext) {
        try {
          const loseContext = gl.getExtension("WEBGL_lose_context") as
            | { loseContext?: () => void }
            | null;
          loseContext?.loseContext?.();
        } catch {}
      }
    }
    this.webglPrograms.clear();
    this.webglBuffer = null;
    this.gl = null;
  }

  private disposeWebGpuResources(): void {
    if (!this.webgpu) return;
    this.webgpu.programById.clear();
    this.webgpu.ready = false;
    this.webgpu.failed = true;
    this.webgpu.initPromise = null;
  }
}
